import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button, Container, Stack, Text } from '@mantine/core';
import { notifications } from '@mantine/notifications';
import PageHeader from '@components/shared/PageHeader';
import { AdminUsersTable } from './components/AdminUsersTable';
import {
  verifyAllMigratedLegacyUsers,
  type LegacyMigratedBatchVerifyResult,
} from '../services/legacyUserManager';
import { useAuthUser } from '../providers/AuthProvider';
import { canAccessLegacyUsersAdmin } from '../config/env';
import type { Users } from '../types/database';

export default function AdminUsers() {
  const navigate = useNavigate();
  const authUser = useAuthUser();
  const legacyAdmin = canAccessLegacyUsersAdmin(authUser?.email);
  const [checkingMigrated, setCheckingMigrated] = useState(false);
  const [lastBatch, setLastBatch] = useState<LegacyMigratedBatchVerifyResult | null>(
    null
  );

  const handleRowClick = (user: Users) => {
    navigate(`/admin/users/${user.id}`);
  };

  const handleCheckMigrated = async () => {
    setCheckingMigrated(true);
    setLastBatch(null);
    try {
      const result = await verifyAllMigratedLegacyUsers();
      setLastBatch(result);

      if (result.ok) {
        notifications.show({
          color: 'teal',
          title: 'Migrated users OK',
          message: `Verify PASS su ${result.passed}/${result.scanned} utenti migrati`,
        });
        return;
      }

      const ids = result.failing_legacy_user_ids.join(', ');
      notifications.show({
        color: 'red',
        title: `Verify FAIL · ${result.failed} utente/i`,
        message: `Legacy ID che falliscono: ${ids}. Primo: ${result.first_failing_legacy_user_id}`,
        autoClose: 12000,
      });
    } catch (err) {
      notifications.show({
        color: 'red',
        title: 'Check migrati fallito',
        message: err instanceof Error ? err.message : 'Unknown error',
      });
    } finally {
      setCheckingMigrated(false);
    }
  };

  return (
    <Container size='2xl' py='lg'>
      <Stack gap='lg'>
        <PageHeader
          title='Users Management'
          breadcrumbs={[{ label: 'Admin', href: '/admin' }, { label: 'Users' }]}
          showBackButton={false}
          actions={
            legacyAdmin ? (
              <Button
                variant='light'
                loading={checkingMigrated}
                onClick={handleCheckMigrated}
              >
                Check user migrati
              </Button>
            ) : undefined
          }
        />
        {legacyAdmin && lastBatch && !lastBatch.ok ? (
          <Text size='sm' c='red'>
            Fail verify — legacy IDs:{' '}
            {lastBatch.failing_legacy_user_ids.map((id, idx) => (
              <Text
                key={id}
                span
                style={{ cursor: 'pointer', textDecoration: 'underline' }}
                onClick={() => navigate(`/admin/legacy-users/${id}`)}
              >
                {id}
                {idx < lastBatch.failing_legacy_user_ids.length - 1 ? ', ' : ''}
              </Text>
            ))}
            {lastBatch.first_failing_legacy_user_id != null ? (
              <>
                {' '}
                · primo:{' '}
                <Text
                  span
                  fw={700}
                  style={{ cursor: 'pointer', textDecoration: 'underline' }}
                  onClick={() =>
                    navigate(
                      `/admin/legacy-users/${lastBatch.first_failing_legacy_user_id}`
                    )
                  }
                >
                  {lastBatch.first_failing_legacy_user_id}
                </Text>
              </>
            ) : null}
          </Text>
        ) : null}
        <AdminUsersTable onRowClick={handleRowClick} />
      </Stack>
    </Container>
  );
}
